import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  Space,
  Input,
  Form,
  Drawer,
  Table,
  Tag,
  Select,
} from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import Address from "CommonComponents/Address";
import {
  getCompanyListAPI,
  addCompanyAPI,
  updateCompanyAPI,
} from "CommonComponents/Lookup/LookupAPI";
import AttachmentComponent from "CommonComponents/Attachments/Attachments";
import FormType from "features/Enums/FormType";
import { FormattedMessage, useIntl } from "react-intl";
import CountryCode from "CommonComponents/CountryCode";
import { MinusCircleOutlined } from "@ant-design/icons";

function MappedCompany() {
  const quillRef = useRef(null);
  const { Column } = Table;
  const intl = useIntl();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [companyId, setCompanyId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [attachmentCompanyId, setAttachmentCompanyId] = useState(null);
  const [isFieldsEdited, setIsFieldsEdited] = useState(false);
  const companies = useSelector((state) => state.lookup.companies);
  const totalItems = useSelector((state) => state.lookup.totalItems);
  const { Search } = Input;
  const pageSize = 10;
  const formTypeId = Object.values(FormType).find(
    (f) => f.name === "MappedCompany"
  )?.id;

  // Open drawer for add
  const showDrawer = () => {
    setEditMode(false);
    setCompanyId(null);
    form.resetFields();
    form.setFieldsValue({
      services: [{ nameEn: "", nameFr: "" }],
      termsAndConditions: "",
    });
    setAttachmentCompanyId(null);
    setOpen(true);
  };

  const handleEdit = (company) => {
    setEditMode(true);
    setOpen(true);
    setCompanyId(company.id);
    setAttachmentCompanyId(company.id);
    form.setFieldsValue({
      ...company,
      status: company.isActive,
      termsAndConditions: company.termsAndConditions || "",
      primaryMobile: company.primaryMobile
        ? company.primaryMobile.replace(/[^0-9]/g, "").slice(-10)
        : "",
      secondaryMobile: company.secondaryMobile
        ? company.secondaryMobile.replace(/[^0-9]/g, "").slice(-10)
        : "",
      primaryMobileCountryCode: company.primaryMobileCountryCode || "",
      secondaryMobileCountryCode: company.secondaryMobileCountryCode || "",
      Company: {
        address1: company.addressLine1,
        address2: company.addressLine2,
        city: company.city,
        provinceId: company.provinceId,
        countryId: company.countryId,
        zip: company.zip,
        id: company.addressId,
      },
      services:
        Array.isArray(company.services) && company.services.length > 0
          ? company.services
              .filter((s) => s.isActive !== false)
              .map((s) => ({
                id: s.id,
                nameEn: s.nameEn || "",
                nameFr: s.nameFr || "",
              }))
          : [{ nameEn: "", nameFr: "" }],
    });
    setTimeout(() => {
      if (quillRef.current && company.termsAndConditions) {
        const quill = quillRef.current.getEditor();
        quill.clipboard.dangerouslyPasteHTML(company.termsAndConditions);
      }
    }, 100);
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
    setCompanyId(null);
    setEditMode(false);
    form.resetFields();
  };

  const handleFinish = async (values) => {
    let submitValues = { ...values };
    if (companyId) {
      submitValues.id = companyId;
    }
    submitValues.isActive = submitValues.status;

    if (values.Company) {
      const { id, ...companyAddress } = values.Company;
      submitValues = {
        ...submitValues,
        addressLine1: companyAddress.address1,
        addressLine2: companyAddress.address2,
        city: companyAddress.city,
        provinceId: companyAddress.provinceId,
        countryId: companyAddress.countryId,
        zip: companyAddress.zip,
      };
      delete submitValues.Company;
    }
    onClose();

    const response = await (editMode
      ? dispatch(updateCompanyAPI(submitValues))
      : dispatch(addCompanyAPI(submitValues)));

    const newId = response?.id;
    if (newId) {
      onClose();
      setCompanyId(newId);
      dispatch(
        getCompanyListAPI({
          PageNo: currentPage,
          PageSize: pageSize,
          SearchQuery: searchTerm,
        })
      );
    }
  };

  useEffect(() => {
    dispatch(
      getCompanyListAPI({
        PageNo: currentPage,
        PageSize: pageSize,
        SearchQuery: searchTerm,
      })
    );
  }, [dispatch, currentPage, pageSize, searchTerm]);

  return (
    <>
      <>
        <Row justify="space-between" align="top" gutter={[16, 8]}>
          <Col xs={24} sm={12} md={12} lg={8} xl={8}>
            <Search
              placeholder={intl.formatMessage({
                id: "MappedCompany.SearchWithNamePhoneNumberEmail",
              })}
              allowClear
              onSearch={setSearchTerm}
            />
          </Col>
          <Col xs={24} sm={12} md={12} lg={8} xl={8} className="text-right">
            <Button type="primary" onClick={showDrawer}>
              <PlusCircleOutlined />
              {<FormattedMessage id="MappedCompany.AddCompany" />}
            </Button>
          </Col>
        </Row>
        <Table
          dataSource={companies.items}
          bordered
          className="mt-10"
          scroll={{ x: 1100 }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalItems,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
          }}
          rowKey="id"
        >
          <Column title="Id" dataIndex="id" key="id" />
          <Column
            title={<FormattedMessage id="Common.Name" />}
            dataIndex="companyName"
            key="companyName"
          />
          <Column
            title={<FormattedMessage id="Common.PhoneNumber" />}
            key="primaryMobile"
            render={(_, record) => (
              <>
                {record.primaryMobileCountryCode
                  ? `${record.primaryMobileCountryCode} `
                  : ""}
                {record.primaryMobile}
              </>
            )}
          />
          <Column
            title={<FormattedMessage id="Common.Email" />}
            dataIndex="primaryEmail"
            key="primaryEmail"
          />
          <Column
            title={<FormattedMessage id="Common.Address" />}
            key="address"
            render={(_, record) => (
              <>
                {record.addressLine1 && <div>{record.addressLine1}</div>}
                {record.addressLine2 && <div>{record.addressLine2}</div>}
                <div>
                  {record.city}
                  {record.provinceName ? `, ${record.provinceName}` : ""}
                  {record.countryName ? `, ${record.countryName}` : ""}
                  {record.zip ? ` - ${record.zip}` : ""}
                </div>
              </>
            )}
          />
          <Column
            title={<FormattedMessage id="Common.Status" />}
            dataIndex="isActive"
            key="isActive"
            width={100}
            render={(isActive) => (
              <Tag color={isActive ? "#6FCF97" : "#EB5757"}>
                {isActive ? (
                  <FormattedMessage id="Common.Active" />
                ) : (
                  <FormattedMessage id="Common.InActive" />
                )}
              </Tag>
            )}
          />
          <Column
            title={<FormattedMessage id="Common.Action" />}
            key="action"
            width={80}
            render={(_, record) => (
              <Space size="middle">
                <Button size="small" onClick={() => handleEdit(record)}>
                  <EditOutlined />
                </Button>
                {/* <Button size="small" danger>
                  <DeleteOutlined />
                </Button> */}
              </Space>
            )}
          />
        </Table>
        <Drawer
          title={
            editMode ? (
              <FormattedMessage id="MappedCompany.Editmappedcompany" />
            ) : (
              <FormattedMessage id="MappedCompany.AddCompany" />
            )
          }
          closable={{ "aria-label": "Close Button" }}
          onClose={onClose}
          open={open}
          width={600}
        >
          <Form
            form={form}
            name="basic"
            layout="vertical"
            autoComplete="off"
            size="large"
            onFinish={handleFinish}
            initialValues={{
              services: [{ nameEn: "", nameFr: "" }],
            }}
          >
            <Row gutter={16} align="middle">
              <Col xs={24} sm={6}>
                <Form.Item
                  label={<FormattedMessage id="MappedCompany.CompanyLogo" />}
                  name="companyLogo"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "MappedCompany.CompanyLogoisrequired",
                      }),
                    },
                  ]}
                >
                  <AttachmentComponent
                    // key={attachmentCompanyId}
                    identifier={formTypeId}
                    currentId={attachmentCompanyId}
                    getattachments={open}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={18}>
                <Form.Item
                  label={<FormattedMessage id="MappedCompany.CompanyName" />}
                  name="companyName"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "MappedCompany.CompanyNameisrequired",
                      }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "MappedCompany.Entercompanyname",
                    })}
                  />
                </Form.Item>
                <Form.Item
                  label={
                    <FormattedMessage id="MappedCompany.RegistrationNumber" />
                  }
                  name="registrationNumber"
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "MappedCompany.EnterRegistrationNumber",
                    })}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                <Form.Item
                  label={<FormattedMessage id="AddClient.PhoneNumber" />}
                  name="primaryMobile"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "AddClient.error.PleaseEnterPhoneNumber",
                      }),
                    },
                    {
                      pattern: /^[0-9]{10,11}$/,
                      message: intl.formatMessage({
                        id: "AddClient.error.Phonenumbermustbe10digits",
                      }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "AddClient.placeholder.EnterPhoneNumber",
                    })}
                    addonBefore={
                      <CountryCode name="primaryMobileCountryCode" />
                    }
                    style={{ width: "100%" }}
                    maxLength={11}
                    type="tel"
                    value={form.getFieldValue("primaryMobile")}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      form.setFieldsValue({ primaryMobile: value });
                    }}
                    onPaste={(event) => {
                      const pasteData = event.clipboardData.getData("Text");
                      if (!/^\d+$/.test(pasteData)) {
                        event.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                <Form.Item
                  label={
                    <FormattedMessage id="MappedComapny.SecondaryPhoneNumber" />
                  }
                  name="secondaryMobile"
                  rules={[
                    {
                      pattern: /^[0-9]{10,11}$/,
                      message: intl.formatMessage({
                        id: "AddClient.error.Phonenumbermustbe10digits",
                      }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "AddClient.placeholder.EnterPhoneNumber",
                    })}
                    addonBefore={
                      <CountryCode name="secondaryMobileCountryCode" />
                    }
                    style={{ width: "100%" }}
                    maxLength={11}
                    type="tel"
                    value={form.getFieldValue("secondaryMobile")}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      form.setFieldsValue({ secondaryMobile: value });
                    }}
                    onPaste={(event) => {
                      const pasteData = event.clipboardData.getData("Text");
                      if (!/^\d+$/.test(pasteData)) {
                        event.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={<FormattedMessage id="MappedCompany.PrimaryEmail" />}
                  name="primaryEmail"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "MappedCompany.PrimaryEmailisrequired",
                      }),
                    },
                    {
                      type: "email",
                      message: intl.formatMessage({
                        id: "Lead.Rule.Email",
                      }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "MappedCompany.Enterprimaryemail",
                    })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={<FormattedMessage id="MappedCompany.SecondaryEmail" />}
                  name="secondaryEmail"
                  rules={[
                    {
                      type: "email",
                      message: intl.formatMessage({
                        id: "Lead.Rule.Email",
                      }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "MappedCompany.EnterSecondaryEmail",
                    })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24}>
                <Form.Item>
                  <Address
                    prefix="Company"
                    setIsFieldsEdited={setIsFieldsEdited}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label={<FormattedMessage id="MappedCompany.Fax" />}
                  name="fax"
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "MappedCompany.EnterFax",
                    })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={<FormattedMessage id="MappedCompany.HSTNumber" />}
                  name="hstnumber"
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "MappedCompany.EnterHSTNumber",
                    })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                {companyId && (
                  <Form.Item
                    label={<FormattedMessage id="Common.Status" />}
                    name="status"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "ProductService.Pleaseselectstatus",
                        }),
                      },
                    ]}
                  >
                    <Select>
                      <Select.Option value={true}>
                        {<FormattedMessage id="Common.Active" />}
                      </Select.Option>
                      <Select.Option value={false}>
                        {<FormattedMessage id="Common.InActive" />}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                )}
              </Col>
              <Col xs={24} sm={24}>
                <Form.List
                  name="services"
                  rules={[
                    {
                      validator: async (_, services) => {
                        if (!services || services.length === 0) {
                          return Promise.reject(
                            new Error("Please add at least one service")
                          );
                        }
                      },
                    },
                  ]}
                >
                  {(fields, { add, remove }) => (
                    <>
                      <label style={{ fontWeight: 500 }}>
                        <span style={{ color: "#ff4d4f", fontSize: "16px" }}>
                          {" "}
                          *
                        </span>
                        &nbsp;
                        {
                          <FormattedMessage id="MappedCompany.ServicesProvided" />
                        }
                      </label>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <Row gutter={16} key={key} align="middle">
                          <Col flex="1">
                            <Form.Item
                              {...restField}
                              name={[name, "nameEn"]}
                              rules={[
                                {
                                  required: true,
                                  message: intl.formatMessage({
                                    id: "MappedCompany.Placeholder.EnterEnglishName",
                                  }),
                                },
                              ]}
                            >
                              <Input
                                placeholder={intl.formatMessage({
                                  id: "MappedCompany.EnglishName",
                                })}
                                maxLength={25}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex="1">
                            <Form.Item
                              {...restField}
                              name={[name, "nameFr"]}
                              rules={[
                                {
                                  required: true,
                                  message: intl.formatMessage({
                                    id: "MappedCompany.Placeholder.EnterFrenchName",
                                  }),
                                },
                              ]}
                            >
                              <Input
                                placeholder={intl.formatMessage({
                                  id: "MappedCompany.FrenchName",
                                })}
                                maxLength={25}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex="32px">
                            {index > 0 && (
                              <MinusCircleOutlined
                                onClick={() => remove(name)}
                                style={{
                                  color: "red",
                                  fontSize: 18,
                                  cursor: "pointer",
                                }}
                              />
                            )}
                          </Col>
                        </Row>
                      ))}
                      <Form.Item>
                        <a onClick={() => add()} style={{ color: "#1890ff" }}>
                          +{" "}
                          {<FormattedMessage id="MappedCompany.AddServices" />}
                        </a>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Col>
              <Col
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                className="estimate-quill"
              >
                <Form.Item
                  label={
                    <FormattedMessage id="MappedCompany.TermsAndConditions" />
                  }
                  name="termsAndConditions"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "MappedCompany.Error.TermsAndConditions",
                      }),
                    },
                  ]}
                >
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    onChange={(content) => {
                      form.setFieldsValue({ termsAndConditions: content });
                    }}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, false] }],
                        ["bold", "italic", "underline", "strike"],
                        ["blockquote", "code-block"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["clean"],
                      ],
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24} className="text-center mt-10">
                <Space>
                  <Button
                    danger
                    variant="outlined"
                    size="medium"
                    onClick={onClose}
                  >
                    <CloseCircleOutlined />{" "}
                    {<FormattedMessage id="Common.Cancel" />}
                  </Button>
                  <Button type="primary" size="medium" htmlType="submit">
                    <CheckCircleOutlined />{" "}
                    {editMode ? (
                      <FormattedMessage id="Common.Save" />
                    ) : (
                      <FormattedMessage id="MappedCompany.AddCompany" />
                    )}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Drawer>
      </>
    </>
  );
}

export default MappedCompany;
